/**
 * Fuzzy matching utilities for CSV deduplication
 * Ported from backend/src/routes/history.ts
 */

/**
 * Normalize text for comparison (lowercase, trim)
 */
export function normalizeText(text: string): string {
  return text.trim().toLowerCase();
}

/**
 * Generate word variations for fuzzy matching (singular/plural handling)
 */
export function getWordVariations(text: string): string[] {
  const variations = new Set<string>();
  const normalizedText = normalizeText(text);

  // Original text (exact match)
  variations.add(text);

  // Case-insensitive variation
  variations.add(normalizedText);

  // Singular/plural handling and common patterns
  if (normalizedText.endsWith('s') && normalizedText.length > 1) {
    // Try removing 's' for potential plural
    variations.add(normalizedText.slice(0, -1));

    // Handle -es endings (boxes -> box)
    if (normalizedText.endsWith('es') && normalizedText.length > 2) {
      variations.add(normalizedText.slice(0, -2));
    }

    // Handle -ies endings (flies -> fly)
    if (normalizedText.endsWith('ies') && normalizedText.length > 3) {
      variations.add(normalizedText.slice(0, -3) + 'y');
    }

    // Handle -ves endings (leaves -> leaf)
    if (normalizedText.endsWith('ves') && normalizedText.length > 3) {
      variations.add(normalizedText.slice(0, -3) + 'f');
    }
  } else {
    // Try adding 's' for potential singular
    variations.add(normalizedText + 's');

    // Try adding 'es' for words ending in certain letters
    if (normalizedText.endsWith('x') || normalizedText.endsWith('s') ||
        normalizedText.endsWith('sh') || normalizedText.endsWith('ch') ||
        normalizedText.endsWith('z')) {
      variations.add(normalizedText + 'es');
    }

    // Handle y -> ies transformation (fly -> flies)
    if (normalizedText.endsWith('y') && normalizedText.length > 1) {
      const beforeY = normalizedText.charAt(normalizedText.length - 2);
      if (!/[aeiou]/.test(beforeY)) { // consonant before y
        variations.add(normalizedText.slice(0, -1) + 'ies');
      }
    }

    // Handle f -> ves transformation (leaf -> leaves)
    if (normalizedText.endsWith('f')) {
      variations.add(normalizedText.slice(0, -1) + 'ves');
    }
  }

  return Array.from(variations);
}

/**
 * Check if a new English word is a fuzzy match of existing words (prevents CSV duplicates)
 */
export function isFuzzyMatchInList(newWord: string, existingWords: string[]): boolean {
  const newWordVariations = getWordVariations(newWord);
  const normalizedExistingWords = existingWords.map(w => normalizeText(w));

  // Check if any variation of the new word matches any existing word
  for (const variation of newWordVariations) {
    const normalizedVariation = normalizeText(variation);
    if (normalizedExistingWords.includes(normalizedVariation)) {
      return true;
    }
  }

  return false;
}

/**
 * Parse CSV string of words into array
 */
export function parseWordList(csv: string): string[] {
  return csv.split(',').map(w => w.trim()).filter(w => w);
}

/**
 * Join word array into CSV string
 */
export function joinWordList(words: string[]): string {
  return words.join(', ');
}
